import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  PaperAirplaneIcon,
  UsersIcon,
  UserIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ArrowPathIcon,
  PhotoIcon,
  LinkIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { uploadChatFile } from '../api/operator';
import {
  sendMessageToAll,
  sendMessageToUser,
  sendMessageToMultiple,
  getSystemChatsInfo,
  CHAT_TYPES,
  CHAT_TYPE_NAMES,
} from '../api/systemChatsAPI';
import LangTabs from './LangTabs';

const AUDIENCES = [
  { id: 'all', label: 'Всем', hint: 'Все пользователи приложения', icon: UsersIcon },
  { id: 'user', label: 'Одному', hint: 'По ID пользователя', icon: UserIcon },
  { id: 'multiple', label: 'Нескольким', hint: 'Список ID', icon: UserGroupIcon },
];

const MAX_LENGTH = 2000;
const MAX_IMAGE_MB = 10;
const MAX_ACTION_LABEL = 40;
const DEFAULT_ACTION_LABEL = 'Подробнее';
// Если RU-кнопка «Подробнее» и перевод пуст — бэкенд сам подставит это на языке получателя
const DEFAULT_LABELS = { ru: DEFAULT_ACTION_LABEL, kk: 'Толығырақ', en: 'Learn more' };
const EMPTY_TR = { kk: '', en: '' };
const langLabel = (l) => (l === 'kk' ? 'KZ' : l.toUpperCase());

/** Бос — батырма жоқ; әйтпесе тек https:// сілтеме */
const actionUrlError = (url) => {
  const v = url.trim();
  if (!v) return 'Укажите ссылку';
  try {
    const u = new URL(v);
    if (u.protocol !== 'https:') return 'Ссылка должна начинаться с https://';
  } catch {
    return 'Некорректная ссылка';
  }
  return v.length > 500 ? 'Ссылка длиннее 500 символов' : null;
};

const inputCls =
  'w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2.5 text-sm text-text-primary ' +
  'placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30';

const previewBtnCls =
  'flex items-center justify-center gap-1.5 border-t border-border-primary px-3.5 py-2.5 text-sm font-semibold text-[#4F45E5]';

const fmt = (n) => Number(n || 0).toLocaleString('ru-RU');

/** "123, 456 789\n12" → [123, 456, 789, 12] (без дублей) */
const parseIds = (text) => [
  ...new Set(
    String(text || '')
      .split(/[\s,;]+/)
      .map((s) => parseInt(s, 10))
      .filter((n) => Number.isInteger(n) && n > 0)
  ),
];

const SystemChatManagement = () => {
  const [audience, setAudience] = useState('all');
  const [chatType, setChatType] = useState(CHAT_TYPES.PROMOTIONS);
  const [message, setMessage] = useState('');
  // Перевод текста (KZ/EN); каждый получатель видит язык своего устройства, пусто — RU
  const [msgTr, setMsgTr] = useState(EMPTY_TR);
  const [labelTr, setLabelTr] = useState(EMPTY_TR);
  const [lang, setLang] = useState('ru');
  const [userId, setUserId] = useState('');
  const [userIds, setUserIds] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [result, setResult] = useState(null); // { type, text }
  const [systemChatsInfo, setSystemChatsInfo] = useState([]);
  const [infoState, setInfoState] = useState('loading');
  // Сурет таңдалған сәтте жүктеледі — «Отправить» кезінде тек URL жіберіледі
  const [image, setImage] = useState(null); // { name, preview, url, uploading, error }
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  // Көпіршік астындағы батырма («Подробнее» → акция беті сайтта/қосымшада)
  const [actionOn, setActionOn] = useState(false);
  const [actionUrl, setActionUrl] = useState('');
  const [actionLabel, setActionLabel] = useState(DEFAULT_ACTION_LABEL);

  useEffect(() => () => image?.preview && URL.revokeObjectURL(image.preview), [image?.preview]);

  const attachImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setResult({ type: 'error', text: 'Можно прикрепить только изображение (JPG, PNG, WebP).' });
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      setResult({ type: 'error', text: `Изображение больше ${MAX_IMAGE_MB} МБ.` });
      return;
    }
    const preview = URL.createObjectURL(file);
    setResult(null);
    setConfirmAll(false);
    setImage({ name: file.name, preview, url: null, uploading: true, error: null });
    try {
      const url = await uploadChatFile(file);
      setImage((cur) => (cur?.preview === preview ? { ...cur, url, uploading: false } : cur));
    } catch (error) {
      console.error('Image upload failed:', error);
      setImage((cur) =>
        cur?.preview === preview ? { ...cur, uploading: false, error: error.message || 'Ошибка загрузки' } : cur
      );
    }
  };

  const removeImage = () => {
    setImage(null);
    setConfirmAll(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  useEffect(() => {
    (async () => {
      try {
        const data = await getSystemChatsInfo();
        setSystemChatsInfo(data.system_chats || []);
        setInfoState('ready');
      } catch (error) {
        console.error('Error fetching system chats info:', error);
        setInfoState('error');
      }
    })();
  }, []);

  const infoByType = useMemo(
    () => Object.fromEntries(systemChatsInfo.map((c) => [c.chat_type, c])),
    [systemChatsInfo]
  );

  const ids = useMemo(() => parseIds(userIds), [userIds]);
  const trimmed = message.trim();
  const msgValue = lang === 'ru' ? message : msgTr[lang];
  const setMsgValue = (v) => (lang === 'ru' ? setMessage(v) : setMsgTr((t) => ({ ...t, [lang]: v })));
  const labelValue = lang === 'ru' ? actionLabel : labelTr[lang];
  const setLabelValue = (v) => (lang === 'ru' ? setActionLabel(v) : setLabelTr((t) => ({ ...t, [lang]: v })));
  const langStatus = { ru: trimmed ? 'filled' : 'empty', kk: msgTr.kk.trim() ? 'filled' : 'empty', en: msgTr.en.trim() ? 'filled' : 'empty' };
  const trTooLong = ['kk', 'en'].some((l) => msgTr[l].trim().length > MAX_LENGTH);
  // Предпросмотр на выбранном языке (как увидит получатель с этим языком)
  const previewText = (lang !== 'ru' && msgTr[lang].trim()) || trimmed;
  const previewLabel =
    (lang !== 'ru' && labelTr[lang].trim()) ||
    (lang !== 'ru' && (actionLabel.trim() || DEFAULT_ACTION_LABEL) === DEFAULT_ACTION_LABEL ? DEFAULT_LABELS[lang] : actionLabel.trim() || DEFAULT_ACTION_LABEL);
  const recipientsOk =
    audience === 'all' || (audience === 'user' ? /^\d+$/.test(userId.trim()) : ids.length > 0);
  const imageReady = Boolean(image?.url);
  const hasContent = trimmed.length > 0 || imageReady;
  const actionError = actionOn
    ? actionUrlError(actionUrl) ||
      ([actionLabel, labelTr.kk, labelTr.en].some((v) => v.trim().length > MAX_ACTION_LABEL) ? 'Слишком длинный текст кнопки' : null)
    : null;
  const canSend =
    hasContent &&
    trimmed.length <= MAX_LENGTH &&
    !trTooLong &&
    recipientsOk &&
    !loading &&
    !image?.uploading &&
    !image?.error &&
    !actionError;

  const send = async () => {
    setLoading(true);
    setResult(null);
    // Сурет + мәтін = бір хабарлама: message_type "image", file_url — сурет, message — астындағы мәтін
    const base = imageReady
      ? { message: trimmed, message_type: 'image', file_url: image.url, file_name: image.name, chat_type: chatType }
      : { message: trimmed, message_type: 'text', chat_type: chatType };
    const translations = Object.fromEntries(['kk', 'en'].filter((l) => msgTr[l].trim()).map((l) => [l, msgTr[l].trim()]));
    if (Object.keys(translations).length) base.translations = translations;
    if (actionOn) {
      base.action_url = actionUrl.trim();
      base.action_label = actionLabel.trim() || DEFAULT_ACTION_LABEL;
      const labels = Object.fromEntries(['kk', 'en'].filter((l) => labelTr[l].trim()).map((l) => [l, labelTr[l].trim()]));
      if (Object.keys(labels).length) base.action_label_translations = labels;
    }
    try {
      if (audience === 'all') {
        const r = await sendMessageToAll(base);
        setResult({
          type: r.error_count ? 'warning' : 'success',
          text: `Отправлено ${fmt(r.sent_count)} из ${fmt(r.total_users)}${r.error_count ? `, ошибок: ${fmt(r.error_count)}` : ''}.`,
        });
      } else if (audience === 'user') {
        const r = await sendMessageToUser({ ...base, user_id: parseInt(userId, 10) });
        setResult({ type: 'success', text: `Сообщение отправлено пользователю #${r.user_id} в чат «${r.chat_name}».` });
        setUserId('');
      } else {
        const r = await sendMessageToMultiple({ ...base, user_ids: ids });
        setResult({
          type: r.error_count ? 'warning' : 'success',
          text:
            `Отправлено ${fmt(r.sent_count)} из ${fmt(r.total_users)}` +
            (r.errors?.length ? `. Ошибки: ${r.errors.join(', ')}` : '.'),
        });
        setUserIds('');
      }
      setMessage('');
      setMsgTr(EMPTY_TR);
      setLabelTr(EMPTY_TR);
      setLang('ru');
      removeImage();
      setActionOn(false);
      setActionUrl('');
      setActionLabel(DEFAULT_ACTION_LABEL);
    } catch (error) {
      setResult({ type: 'error', text: error.message || 'Ошибка при отправке сообщения' });
    } finally {
      setLoading(false);
      setConfirmAll(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSend) return;
    // Рассылка всем — только после явного подтверждения
    if (audience === 'all' && !confirmAll) {
      setConfirmAll(true);
      return;
    }
    send();
  };

  const resultTone = {
    success: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-500',
    warning: 'border-amber-500/30 bg-amber-500/10 text-amber-500',
    error: 'border-rose-500/30 bg-rose-500/10 text-rose-500',
  };

  return (
    <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-5">
      <form onSubmit={handleSubmit} className="space-y-5 rounded-block border border-border-primary bg-bg-card p-5 shadow-sm xl:col-span-3">
        {/* Audience */}
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-text-primary">Получатели</legend>
          <div className="grid grid-cols-3 gap-2">
            {AUDIENCES.map((a) => {
              const Icon = a.icon;
              const active = audience === a.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    setAudience(a.id);
                    setConfirmAll(false);
                  }}
                  aria-pressed={active}
                  className={`rounded-lg border p-3 text-left transition-colors ${
                    active ? 'border-[#4F45E5] bg-[#4F45E5]/10 ring-1 ring-[#4F45E5]/40' : 'border-border-primary hover:bg-bg-tertiary'
                  }`}
                >
                  <Icon className={`mb-1.5 h-5 w-5 ${active ? 'text-[#7C3AED]' : 'text-text-muted'}`} />
                  <span className="block text-sm font-semibold text-text-primary">{a.label}</span>
                  <span className="block text-xs text-text-secondary">{a.hint}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        {audience === 'user' && (
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-text-primary">ID пользователя</span>
            <input
              type="text"
              inputMode="numeric"
              value={userId}
              onChange={(e) => setUserId(e.target.value.replace(/\D/g, ''))}
              placeholder="Например: 1141"
              className={`${inputCls} font-mono`}
            />
          </label>
        )}

        {audience === 'multiple' && (
          <label className="block">
            <span className="mb-1.5 flex items-baseline justify-between text-sm font-medium text-text-primary">
              ID пользователей
              <span className="text-xs font-normal text-text-muted">распознано: {ids.length}</span>
            </span>
            <textarea
              value={userIds}
              onChange={(e) => setUserIds(e.target.value)}
              rows={3}
              placeholder="Через запятую, пробел или с новой строки: 123, 456, 789"
              className={`${inputCls} font-mono`}
            />
          </label>
        )}

        {/* Chat type */}
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-text-primary">От имени чата</legend>
          <div className="flex flex-wrap gap-2">
            {Object.values(CHAT_TYPES).map((type) => {
              const active = chatType === type;
              const info = infoByType[type];
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => setChatType(type)}
                  aria-pressed={active}
                  title={info ? `${fmt(info.total_chats)} чатов · ${fmt(info.total_messages)} сообщений` : undefined}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                    active
                      ? 'border-[#4F45E5]/50 bg-[#4F45E5]/15 text-text-primary'
                      : 'border-border-primary text-text-secondary hover:bg-bg-tertiary'
                  }`}
                >
                  {CHAT_TYPE_NAMES[type]}
                  {info && <span className="ml-1.5 font-normal text-text-muted">{fmt(info.total_chats)}</span>}
                </button>
              );
            })}
          </div>
        </fieldset>

        {/* Image */}
        <div>
          <span className="mb-1.5 block text-sm font-medium text-text-primary">
            Изображение <span className="font-normal text-text-muted">— необязательно</span>
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => attachImage(e.target.files?.[0])}
          />
          {image ? (
            <div className="flex items-center gap-3 rounded-lg border border-border-primary bg-bg-primary p-2">
              <img src={image.preview} alt="" className="h-16 w-16 shrink-0 rounded-md object-cover" />
              <div className="min-w-0 flex-1 text-sm">
                <p className="truncate font-medium text-text-primary">{image.name}</p>
                {image.uploading && (
                  <p className="flex items-center gap-1.5 text-xs text-text-secondary">
                    <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> Загрузка…
                  </p>
                )}
                {image.url && <p className="text-xs text-emerald-500">Загружено</p>}
                {image.error && (
                  <p className="text-xs text-rose-500">
                    {image.error} ·{' '}
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="underline">
                      выбрать другое
                    </button>
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={removeImage}
                aria-label="Убрать изображение"
                className="rounded-lg p-2 text-text-muted hover:bg-rose-500/10 hover:text-rose-500"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                attachImage(e.dataTransfer.files?.[0]);
              }}
              className={`flex w-full items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-4 text-sm transition-colors ${
                dragOver
                  ? 'border-[#4F45E5] bg-[#4F45E5]/10 text-text-primary'
                  : 'border-border-primary text-text-secondary hover:bg-bg-tertiary'
              }`}
            >
              <PhotoIcon className="h-5 w-5 shrink-0" />
              Выберите, перетащите или вставьте (Ctrl+V) изображение · до {MAX_IMAGE_MB} МБ
            </button>
          )}
        </div>

        {/* Language */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <LangTabs value={lang} onChange={setLang} status={langStatus} />
          <span className="text-xs text-text-muted">
            {lang === 'ru' ? 'Каждый получатель увидит текст на языке своего устройства' : `Пусто — получатели с ${langLabel(lang)} увидят RU`}
          </span>
        </div>

        {/* Message */}
        <label className="block">
          <span className="mb-1.5 flex items-baseline justify-between text-sm font-medium text-text-primary">
            <span>
              {image ? 'Подпись к изображению' : 'Сообщение'} <span className="font-normal text-text-muted">· {langLabel(lang)}</span>
            </span>
            <span className={`text-xs font-normal tabular-nums ${msgValue.trim().length > MAX_LENGTH ? 'text-rose-500' : 'text-text-muted'}`}>
              {msgValue.length} / {MAX_LENGTH}
            </span>
          </span>
          <textarea
            value={msgValue}
            onChange={(e) => {
              setMsgValue(e.target.value);
              setConfirmAll(false);
            }}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') handleSubmit(e);
            }}
            onPaste={(e) => {
              const file = [...(e.clipboardData?.files || [])].find((f) => f.type.startsWith('image/'));
              if (file) {
                e.preventDefault();
                attachImage(file);
              }
            }}
            rows={6}
            placeholder={
              lang === 'ru'
                ? image
                  ? 'Текст под изображением (необязательно)…'
                  : 'Текст сообщения…'
                : lang === 'kk'
                ? 'Қазақша мәтін (бос болса — RU көрсетіледі)…'
                : 'English text (empty — RU is shown)…'
            }
            className={`${inputCls} resize-y`}
          />
          <span className="mt-1 block text-xs text-text-muted">Ctrl + Enter — отправить</span>
        </label>

        {/* Action button */}
        <div className="rounded-lg border border-border-primary">
          <label className="flex cursor-pointer items-center justify-between gap-3 px-3 py-2.5">
            <span className="flex items-center gap-2 text-sm font-medium text-text-primary">
              <LinkIcon className="h-4 w-4 text-text-muted" />
              Кнопка под сообщением
              <span className="font-normal text-text-muted">— например, «Подробнее» на страницу акции</span>
            </span>
            <input
              type="checkbox"
              checked={actionOn}
              onChange={(e) => {
                setActionOn(e.target.checked);
                setConfirmAll(false);
              }}
              className="h-4 w-4 accent-[#4F45E5]"
            />
          </label>
          {actionOn && (
            <div className="grid grid-cols-1 gap-3 border-t border-border-primary p-3 sm:grid-cols-3">
              <label className="block sm:col-span-2">
                <span className="mb-1.5 block text-xs font-medium text-text-secondary">Ссылка</span>
                <input
                  type="url"
                  value={actionUrl}
                  onChange={(e) => {
                    setActionUrl(e.target.value);
                    setConfirmAll(false);
                  }}
                  placeholder="https://agroland.kz/news/promotions/1181"
                  className={`${inputCls} font-mono`}
                />
              </label>
              <label className="block">
                <span className="mb-1.5 flex items-baseline justify-between text-xs font-medium text-text-secondary">
                  Текст кнопки · {langLabel(lang)}
                  <span className={`tabular-nums ${labelValue.trim().length > MAX_ACTION_LABEL ? 'text-rose-500' : 'text-text-muted'}`}>
                    {labelValue.length} / {MAX_ACTION_LABEL}
                  </span>
                </span>
                <input
                  type="text"
                  value={labelValue}
                  onChange={(e) => setLabelValue(e.target.value)}
                  placeholder={lang === 'ru' ? DEFAULT_ACTION_LABEL : previewLabel}
                  className={inputCls}
                />
              </label>
              <p className={`text-xs sm:col-span-3 ${actionError && actionUrl.trim() ? 'text-rose-500' : 'text-text-muted'}`}>
                {actionError && actionUrl.trim()
                  ? actionError
                  : 'Ссылки agroland.kz откроются внутри приложения, остальные — в браузере.'}
              </p>
            </div>
          )}
        </div>

        {result && (
          <div role={result.type === 'error' ? 'alert' : 'status'} className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm ${resultTone[result.type]}`}>
            {result.type === 'success' ? <CheckCircleIcon className="h-5 w-5 shrink-0" /> : <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />}
            <span className="flex-1">{result.text}</span>
            <button type="button" onClick={() => setResult(null)} aria-label="Закрыть" className="opacity-70 hover:opacity-100">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}

        {confirmAll ? (
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
            <p className="text-sm text-text-primary">
              <b>Отправить сообщение всем пользователям</b> в чат «{CHAT_TYPE_NAMES[chatType]}»? Отменить рассылку будет нельзя.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="submit"
                autoFocus
                disabled={loading}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 disabled:opacity-60"
              >
                {loading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PaperAirplaneIcon className="h-4 w-4" />}
                {loading ? 'Отправка…' : 'Да, отправить всем'}
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={() => setConfirmAll(false)}
                className="rounded-lg border border-border-primary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-bg-tertiary"
              >
                Отмена
              </button>
            </div>
          </div>
        ) : (
          <button
            type="submit"
            disabled={!canSend}
            className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-accent-grad px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PaperAirplaneIcon className="h-4 w-4" />}
            {loading
              ? 'Отправка…'
              : audience === 'all'
              ? 'Отправить всем'
              : audience === 'user'
              ? 'Отправить пользователю'
              : `Отправить ${ids.length || ''} пользователям`}
          </button>
        )}
      </form>

      {/* Preview + info */}
      <div className="space-y-4 xl:col-span-2">
        <section className="rounded-block border border-border-primary bg-bg-card p-5 shadow-sm">
          <h3 className="mb-3 flex items-center justify-between text-sm font-semibold text-text-primary">
            Предпросмотр <span className="text-xs font-normal text-text-muted">язык получателя: {langLabel(lang)}</span>
          </h3>
          <div className="rounded-lg bg-bg-primary p-4">
            <div className="mb-2 text-xs font-semibold text-text-secondary">{CHAT_TYPE_NAMES[chatType]}</div>
            <div className="max-w-[85%] overflow-hidden rounded-2xl rounded-bl-md bg-bg-card text-sm text-text-primary ring-1 ring-border-primary">
              {image && <img src={image.preview} alt="" className="block max-h-72 w-full object-cover" />}
              <div className="px-3.5 py-2">
                {(previewText || !image) && (
                  <p className="whitespace-pre-wrap break-words">
                    {previewText || <span className="italic text-text-muted">Здесь появится текст сообщения</span>}
                  </p>
                )}
                <div className="mt-1 text-right text-[10px] text-text-muted">
                  {new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              {actionOn &&
                (actionError ? (
                  <div className={`${previewBtnCls} cursor-default opacity-60`}>{previewLabel}</div>
                ) : (
                  <a href={actionUrl.trim()} target="_blank" rel="noopener noreferrer" className={`${previewBtnCls} hover:bg-bg-tertiary`}>
                    {previewLabel}
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                  </a>
                ))}
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-block border border-border-primary bg-bg-card shadow-sm">
          <h3 className="border-b border-border-primary px-5 py-3.5 text-sm font-semibold text-text-primary">Системные чаты</h3>
          {infoState === 'loading' && (
            <ul className="divide-y divide-border-primary">
              {Array.from({ length: 4 }).map((_, i) => (
                <li key={i} className="flex animate-pulse justify-between px-5 py-3">
                  <div className="h-3 w-28 rounded bg-bg-tertiary" />
                  <div className="h-3 w-16 rounded bg-bg-tertiary" />
                </li>
              ))}
            </ul>
          )}
          {infoState === 'error' && <p className="px-5 py-6 text-center text-sm text-text-muted">Не удалось загрузить информацию</p>}
          {infoState === 'ready' && (
            <ul className="divide-y divide-border-primary">
              {systemChatsInfo.map((c) => (
                <li key={c.chat_type} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-text-primary">{c.name}</p>
                    <p className="font-mono text-[11px] text-text-muted">user #{c.system_user_id}</p>
                  </div>
                  <div className="text-right text-xs text-text-secondary">
                    <div><span className="font-semibold tabular-nums text-text-primary">{fmt(c.total_chats)}</span> чатов</div>
                    <div><span className="tabular-nums">{fmt(c.total_messages)}</span> сообщ.</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default SystemChatManagement;
